import axios from 'axios';
import { saveAs } from 'file-saver';
import { useErrorToast, useSuccessToast } from '../hooks/useToast.jsx';
import {
    HTTP_BAD_REQUEST,
    HTTP_CONFLICT,
    HTTP_INTERNAL_ERROR,
    HTTP_NOT_ACCEPTABLE,
    HTTP_UNAUTHORIZED,
    HTTP_UNPROCESSABLE_CONTENT
} from './constants.js';
import { setUser, setUserIsLoading } from '../store/user.js';
import { setError } from '../store/error.js';

const buildResponse = ({ response }) => {
    const { data } = response;

    const httpData = {
        response: null,
        status: response.status,
        success: data.success
    };

    if (data?.data) {
        httpData.response = data?.data;
    }

    if (data instanceof Blob) {
        httpData.response = data;
        httpData.success = true;
    }

    return httpData;
};

const buildErrorMessage = (messages) => {
    let errors = {};

    if (typeof messages === 'string') {
        errors = { [status]: [messages] };
    } else {
        if (typeof messages === 'object') {
            errors = { ...messages };
        }
    }

    return errors;
};

const buildError = ({ response }, api) => {
    if (!('response' in response)) {
        return {
            success: false,
            message: {},
            status: HTTP_INTERNAL_ERROR,
            statusText: 'Something went wrong, please try again or contact the developers.'
        };
    }

    let message = {};

    const { data, status } = response.response;

    let { message: statusText } = data;

    if (status === HTTP_UNAUTHORIZED) {
        localStorage.removeItem('accessToken');
        statusText = null;
    }

    if (status === HTTP_NOT_ACCEPTABLE) {
        localStorage.removeItem('mfaIdentifier');
        const { location } = window;

        if (
            !localStorage.getItem('mfaIdentifier') &&
            !sessionStorage.getItem('mfaIdentifier') &&
            !['/check-email', '/verify-email'].includes(location.pathname)
        ) {
            localStorage.removeItem('accessToken');
            statusText = null;
        } else if (data.data?.user) {
            api.dispatch(setUser(data.data.user));
            api.dispatch(setUserIsLoading(false));

            if (['/check-email', '/verify-email', '/validate', '/two-factor'].includes(location.pathname)) {
                statusText = null;
            }
        }
    }

    if (
        data?.errors &&
        ((typeof data.errors === 'object' && Object.keys(data?.errors).length > 0) ||
            (Array.isArray(data.errors) && data.errors.length > 0))
    ) {
        message = buildErrorMessage(data.errors, status);
        statusText = null;
    }

    return {
        success: false,
        message,
        statusText,
        status
    };
};

const buildFormData = ({ data, formData, parentKey }) => {
    if (data && typeof data === 'object' && !(data instanceof File) && !(data instanceof Blob)) {
        if (Array.isArray(data)) {
            data.forEach((item, index) => {
                const key = parentKey ? `${parentKey}[${index}]` : `${index}`;
                buildFormData({ data: item, formData, parentKey: key });
            });
        } else {
            Object.keys(data).forEach((key) => {
                const nestedKey = parentKey ? `${parentKey}[${key}]` : key;

                buildFormData({ data: data[key], formData, parentKey: nestedKey });
            });
        }
    } else {
        formData.append(parentKey, data);
    }
};

const baseQuery = async (
    { url, method, body, params, fileType, fileName, multipart, toast = true, customURL = null, responseType = 'json' },
    api
) => {
    let baseMethod = method;
    let sendData = body;

    if (method === 'EXPORT') {
        baseMethod = 'GET';
    }

    if (multipart && body) {
        const formData = new FormData();
        buildFormData({ data: body, formData, parentKey: '' });
        sendData = formData;
    }

    const headers = {
        ...(localStorage.getItem('accessToken') && { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }),
        ...(localStorage.getItem('mfaIdentifier') && {
            'x-two-factor-identifier': localStorage.getItem('mfaIdentifier')
        })
    };

    try {
        const config = {
            url: (customURL ?? import.meta.env.VITE_API_ENDPOINT) + url,
            method: baseMethod,
            data: sendData,
            params,
            headers,
            responseType: responseType ?? (method === 'EXPORT' ? 'blob' : 'json')
        };

        const response = await axios(config);

        if (baseMethod !== 'GET' && response.data.message && toast) {
            useSuccessToast(response.data.message);
        }

        if (method === 'EXPORT') {
            saveAs(new Blob([response.data], { type: response.headers['content-type'] }), `${fileName}.${fileType}`);
        }

        return { data: buildResponse({ response }) };
    } catch (error) {
        const response = error;
        const httpResponse = buildError({ response }, api);

        if (httpResponse.status !== HTTP_UNPROCESSABLE_CONTENT && httpResponse.statusText) {
            if (httpResponse.status === HTTP_BAD_REQUEST || httpResponse.status === HTTP_CONFLICT) {
                useErrorToast(httpResponse.statusText);
            } else {
                api.dispatch(setError({ status: httpResponse.status, statusText: httpResponse.statusText }));
            }
        }

        return { error: httpResponse };
    }
};

export { baseQuery, buildResponse, buildError };
